import { authService } from '../../services/auth';
import { onLocaleChange, t } from '../../services/i18n';
import './rc-admin-users';
import './rc-admin-billing-plans';
import './rc-admin-errors';
import './rc-admin-revenue';
import './rc-admin-billing-documents';

type AdminTab = 'users' | 'plans' | 'invoices' | 'credit-notes' | 'revenue' | 'errors';

export class RcAdmin extends HTMLElement {
  private activeTab: AdminTab = 'users';
  private disposeLocaleChange?: () => void;

  connectedCallback(): void {
    this.activeTab = this.readTabFromUrl();
    this.render();
    this.disposeLocaleChange = onLocaleChange(() => this.render());
    this.addEventListener('click', this.handleClick as EventListener);
  }

  disconnectedCallback(): void {
    this.disposeLocaleChange?.();
    this.removeEventListener('click', this.handleClick as EventListener);
  }

  private render(): void {
    if (!authService.isAuthenticated || !authService.isAdmin) {
      this.innerHTML = `<div class="empty-state">${t('admin.accessDenied')}</div>`;
      return;
    }

    const activeView = this.activeTab === 'users'
      ? '<rc-admin-users></rc-admin-users>'
      : this.activeTab === 'plans'
        ? '<rc-admin-billing-plans></rc-admin-billing-plans>'
      : this.activeTab === 'invoices'
        ? '<rc-admin-billing-documents document-type="invoice"></rc-admin-billing-documents>'
      : this.activeTab === 'credit-notes'
        ? '<rc-admin-billing-documents document-type="credit_note"></rc-admin-billing-documents>'
      : this.activeTab === 'revenue'
        ? '<rc-admin-revenue></rc-admin-revenue>'
      : '<rc-admin-errors></rc-admin-errors>';

    this.innerHTML = `
      <div class="page-header">
        <div>
          <h1>${t('pages.admin.title')}</h1>
          <p class="page-subtitle">${t('admin.subtitle')}</p>
        </div>
      </div>
      <div class="admin-tabs" role="tablist" aria-label="${t('pages.admin.title')}">
        <button
          type="button"
          class="admin-tab ${this.activeTab === 'users' ? 'is-active' : ''}"
          data-tab="users"
          role="tab"
          aria-selected="${this.activeTab === 'users'}">
          ${t('admin.tab.users')}
        </button>
        <button
          type="button"
          class="admin-tab ${this.activeTab === 'plans' ? 'is-active' : ''}"
          data-tab="plans"
          role="tab"
          aria-selected="${this.activeTab === 'plans'}">
          ${t('admin.tab.plans')}
        </button>
        <button
          type="button"
          class="admin-tab ${this.activeTab === 'invoices' ? 'is-active' : ''}"
          data-tab="invoices"
          role="tab"
          aria-selected="${this.activeTab === 'invoices'}">
          ${t('admin.tab.invoices')}
        </button>
        <button
          type="button"
          class="admin-tab ${this.activeTab === 'credit-notes' ? 'is-active' : ''}"
          data-tab="credit-notes"
          role="tab"
          aria-selected="${this.activeTab === 'credit-notes'}">
          ${t('admin.tab.creditNotes')}
        </button>
        <button
          type="button"
          class="admin-tab ${this.activeTab === 'revenue' ? 'is-active' : ''}"
          data-tab="revenue"
          role="tab"
          aria-selected="${this.activeTab === 'revenue'}">
          ${t('admin.tab.revenue')}
        </button>
        <button
          type="button"
          class="admin-tab ${this.activeTab === 'errors' ? 'is-active' : ''}"
          data-tab="errors"
          role="tab"
          aria-selected="${this.activeTab === 'errors'}">
          ${t('admin.tab.errors')}
        </button>
      </div>
      ${activeView}
    `;
  }

  private handleClick = (event: Event): void => {
    const target = (event.target as HTMLElement | null)?.closest<HTMLButtonElement>('[data-tab]');
    if (!target) return;

    const tab = target.dataset.tab;
    const requestedTab: AdminTab = tab === 'errors'
      ? 'errors'
      : tab === 'revenue'
        ? 'revenue'
      : tab === 'plans'
        ? 'plans'
      : tab === 'invoices'
        ? 'invoices'
      : tab === 'credit-notes'
        ? 'credit-notes'
        : 'users';
    if (requestedTab === this.activeTab) return;

    this.activeTab = requestedTab;
    const url = new URL(window.location.href);
    if (requestedTab === 'users') {
      url.searchParams.delete('tab');
    } else {
      url.searchParams.set('tab', requestedTab);
    }
    window.history.replaceState({}, '', `${url.pathname}${url.search}`);
    this.render();
  };

  private readTabFromUrl(): AdminTab {
    const tab = new URLSearchParams(window.location.search).get('tab');
    return tab === 'errors' || tab === 'revenue' || tab === 'plans' || tab === 'invoices' || tab === 'credit-notes'
      ? tab
      : 'users';
  }
}

if (!customElements.get('rc-admin')) {
  customElements.define('rc-admin', RcAdmin);
}
